import { useState, useEffect, useRef } from 'react'
import { Zap, TrendingUp, Settings, Loader } from 'lucide-react'
import UserProfile from './UserProfile'
import { useLanguage } from '../context/LanguageContext'

const API_LABELS = {
  google_trends: 'Google Trends',
  amazon:        'Amazon',
  pinterest:     'Pinterest',
  google_maps:   'Google Maps',
  facebook:      'Facebook',
  facebook_rapidapi: 'Facebook (RapidAPI)',
  aliexpress:    'AliExpress',
  google_ads:    'Google Ads',
  dataforseo:    'DataForSEO',
}

function statusColor(s) {
  if (!s) return '#8b949e'
  const st = s.status
  if (st === 'ok')                    return '#3fb950'
  if (st === 'expiring_soon')         return '#d29922'
  if (st === 'configured_not_tested') return '#8b949e'
  if (st === 'not_configured')        return '#4a5568'
  return '#f85149'
}

function statusLabel(s) {
  if (!s) return '...'
  const st = s.status
  if (st === 'ok') {
    if (s.remaining !== undefined) return `OK · ${s.remaining} restantes`
    if (s.days_left !== undefined && s.days_left >= 0) return `OK · ${s.days_left}d`
    return 'OK'
  }
  if (st === 'expiring_soon')         return `Expira en ${s.days_left}d`
  if (st === 'expired')               return 'Token expirado'
  if (st === 'quota_exceeded')        return 'Cuota agotada'
  if (st === 'not_configured')        return 'No configurado'
  if (st === 'configured_not_tested') return 'Configurado'
  if (st === 'error')                 return s.message ? `Error: ${s.message}` : 'Error'
  if (st === 'timeout')               return 'Sin respuesta'
  return st
}

function ApiRow({ name, data, checking }) {
  const color = checking ? '#8b949e' : statusColor(data)
  const label = checking ? '...' : statusLabel(data)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.3rem 0', borderBottom: '1px solid rgba(139,148,158,0.08)'
    }}>
      <span style={{ fontSize: '0.78rem', color: '#c9d1d9', minWidth: '90px' }}>{name}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        {checking
          ? <Loader style={{ width: '0.7rem', height: '0.7rem', color: '#8b949e' }} className="animate-spin" />
          : <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
        }
        <span style={{ fontSize: '0.7rem', color, maxWidth: '130px', textAlign: 'right' }}>{label}</span>
      </div>
    </div>
  )
}

export default function Header({ onNavigate }) {
  const { t } = useLanguage()
  const [showStatus, setShowStatus] = useState(false)
  const [checking, setChecking]     = useState(false)
  const [apis, setApis]             = useState({})
  const [lastOk, setLastOk]         = useState(null) // 'ok' | 'error' | null
  const dropdownRef = useRef(null)

  const runChecks = async () => {
    setChecking(true)
    try {
      const isManual = arguments[0] === true
      const endpoint = isManual ? '/api/health/live' : '/api/health'
      const res = await fetch(endpoint, { signal: AbortSignal.timeout(25000) })
      const data = await res.json()
      setApis(data.apis || {})
      setLastOk(res.ok ? 'ok' : 'error')
    } catch {
      setLastOk('error')
      setApis({})
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => {
    runChecks()
    const iv = setInterval(runChecks, 60_000)
    return () => clearInterval(iv)
  }, [])

  useEffect(() => {
    if (!showStatus) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowStatus(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showStatus])

  // Derive overall indicator color
  const allKeys = Object.keys(API_LABELS)
  const hasError = allKeys.some(k => {
    const s = apis[k]?.status
    return s === 'error' || s === 'quota_exceeded' || s === 'expired'
  })
  const hasWarning = allKeys.some(k => apis[k]?.status === 'expiring_soon')
  const dotColor = checking
    ? '#8b949e'
    : (lastOk === 'error' ? '#f85149' : hasError ? '#f85149' : hasWarning ? '#d29922' : lastOk === 'ok' ? '#3fb950' : '#8b949e')

  return (
    <header style={{
      position: 'relative', zIndex: 500,
      borderBottom: '1px solid rgba(48,54,61,0.7)',
      backdropFilter: 'blur(12px)',
      background: 'linear-gradient(135deg, rgba(13,17,23,0.85) 0%, rgba(22,27,34,0.90) 100%)'
    }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              padding: '0.5rem',
              background: 'linear-gradient(135deg, #21262d, #161b22)',
              border: '1px solid rgba(139,148,158,0.2)',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
            }}>
              <Zap style={{ width: '1.1rem', height: '1.1rem', color: '#e6edf3' }} strokeWidth={2} />
            </div>
            <div>
              <h1 style={{
                fontSize: '1.15rem', fontWeight: 300, letterSpacing: '0.3px',
                fontFamily: "'Space Grotesk','Poppins',sans-serif",
                background: 'linear-gradient(135deg, #e6edf3, #8b949e)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text', margin: 0
              }}>
                Market Research
              </h1>
              <p style={{ fontSize: '0.7rem', color: '#8b949e', margin: 0, fontWeight: 300 }}>
                {t('dashboard.headerSubtitle')}
              </p>
            </div>
          </div>

          {/* Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>

            {/* EN VIVO status button */}
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                onClick={() => { setShowStatus(v => !v); if (!showStatus) runChecks() }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.4rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                  background: hasError ? 'rgba(248,81,73,0.08)' : 'rgba(33,38,45,0.6)',
                  border: hasError ? '1px solid rgba(248,81,73,0.3)' : '1px solid rgba(139,148,158,0.2)',
                  transition: 'all 0.2s', fontFamily: "'Inter',sans-serif"
                }}
              >
                <TrendingUp style={{ width: '0.9rem', height: '0.9rem', color: '#8b949e' }} />
                <span style={{ fontSize: '0.72rem', color: '#c9d1d9', fontWeight: 300 }}>
                  {t('dashboard.liveButton')}
                </span>
                <span style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: dotColor, flexShrink: 0,
                  boxShadow: dotColor === '#3fb950' ? '0 0 4px rgba(63,185,80,0.6)' : 'none'
                }} />
              </button>

              {showStatus && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
                  background: '#161b22', border: '1px solid rgba(139,148,158,0.2)',
                  borderRadius: '10px', padding: '0.75rem', minWidth: '260px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 600
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <p style={{
                      fontSize: '0.68rem', color: '#8b949e', margin: 0,
                      fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em'
                    }}>
                      Estado de APIs
                    </p>
                    {checking && (
                      <Loader style={{ width: '0.7rem', height: '0.7rem', color: '#8b949e' }} className="animate-spin" />
                    )}
                  </div>

                  {Object.entries(API_LABELS).map(([key, name]) => (
                    <ApiRow
                      key={key}
                      name={name}
                      data={apis[key]}
                      checking={checking}
                    />
                  ))}

                  <button
                    onClick={runChecks}
                    disabled={checking}
                    style={{
                      marginTop: '0.6rem', width: '100%', padding: '0.35rem',
                      background: 'rgba(33,38,45,0.8)', border: '1px solid rgba(139,148,158,0.15)',
                      borderRadius: '6px', color: checking ? '#4a5568' : '#8b949e',
                      fontSize: '0.72rem', cursor: checking ? 'not-allowed' : 'pointer',
                      fontFamily: "'Inter',sans-serif", transition: 'all 0.2s'
                    }}
                  >
                    {checking ? 'Verificando...' : 'Verificar ahora'}
                  </button>
                </div>
              )}
            </div>

            {/* Settings */}
            <button
              onClick={() => onNavigate?.('settings')}
              style={{
                padding: '0.4rem',
                background: 'rgba(33,38,45,0.6)',
                border: '1px solid rgba(139,148,158,0.2)',
                borderRadius: '8px', cursor: 'pointer',
                transition: 'all 0.2s', lineHeight: 1
              }}
              title={t('dashboard.settingsTitle')}
            >
              <Settings style={{ width: '1rem', height: '1rem', color: '#8b949e' }} />
            </button>

            <UserProfile onNavigate={onNavigate} />
          </div>
        </div>
      </div>
    </header>
  )
}
